import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Minus, Plus, ShoppingBag, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import PayPalCheckout from "@/components/PayPalCheckout";

export default function Cart() {
  const { items, updateQty, removeItem, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ shipping_name: "", shipping_address: "", shipping_city: "", shipping_zip: "", phone: "", note: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateForm = () => {
    if (!user) { toast.error("Connectez-vous pour commander"); navigate("/connexion"); return false; }
    if (!form.shipping_name || !form.shipping_address || !form.shipping_city || !form.shipping_zip) {
      toast.error("Complétez les informations de livraison"); return false;
    }
    return true;
  };

  const createOrderForPayment = async () => {
    if (!validateForm()) return null;
    const { data: order } = await api.post("/orders", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, color: i.color, variant: i.variant })),
      ...form,
    });
    return order.id;
  };

  const handlePaypalPaid = () => { clear(); navigate("/payment/success?paypal=1"); };

  const checkout = async () => {
    let orderId;
    try {
      orderId = await createOrderForPayment();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création de la commande"); return;
    }
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du paiement");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag className="w-14 h-14 mx-auto text-gray-600" />
        <h1 className="mt-6 font-heading text-3xl font-black">Votre panier est vide</h1>
        <p className="mt-3 text-gray-400">Parcourez la boutique pour trouver votre prochaine création.</p>
        <Link to="/boutique" className="mt-8 inline-flex btn-primary rounded-full px-7 py-3 font-bold">Aller à la boutique</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading text-4xl font-black mb-8">Panier</h1>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it, idx) => (
            <div key={idx} data-testid={`cart-item-${idx}`} className="flex gap-4 rounded-2xl border border-[#222] bg-[#0A0A0A] p-4">
              <img src={it.image || "/images/custom_object.jpeg"} alt={it.name} className="w-24 h-24 rounded-lg object-cover" />
              <div className="flex-1">
                <h3 className="font-heading font-bold">{it.name}</h3>
                <p className="text-sm text-gray-400">{[it.color, it.variant].filter(Boolean).join(" · ")}</p>
                <p className="mt-1 font-black text-[#E6007E]">{it.unit_price.toFixed(2)} €</p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center rounded-full border border-white/20">
                    <button onClick={() => updateQty(idx, it.quantity - 1)} className="p-2"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(idx, it.quantity + 1)} className="p-2"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button data-testid={`cart-remove-${idx}`} onClick={() => removeItem(idx)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="font-black text-white">{(it.unit_price * it.quantity).toFixed(2)} €</div>
            </div>
          ))}

          <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5 space-y-4">
            <h3 className="font-heading font-bold text-lg">Informations de livraison</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Nom complet</Label><Input data-testid="ship-name" value={form.shipping_name} onChange={set("shipping_name")} className="bg-black border-white/15 mt-1" /></div>
              <div><Label>Téléphone</Label><Input data-testid="ship-phone" value={form.phone} onChange={set("phone")} className="bg-black border-white/15 mt-1" /></div>
              <div className="sm:col-span-2"><Label>Adresse</Label><Input data-testid="ship-address" value={form.shipping_address} onChange={set("shipping_address")} className="bg-black border-white/15 mt-1" /></div>
              <div><Label>Ville</Label><Input data-testid="ship-city" value={form.shipping_city} onChange={set("shipping_city")} className="bg-black border-white/15 mt-1" /></div>
              <div><Label>Code postal</Label><Input data-testid="ship-zip" value={form.shipping_zip} onChange={set("shipping_zip")} className="bg-black border-white/15 mt-1" /></div>
            </div>
            <div><Label>Note (optionnel)</Label><Textarea data-testid="ship-note" value={form.note} onChange={set("note")} className="bg-black border-white/15 mt-1" /></div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-6 sticky top-20">
            <h3 className="font-heading font-bold text-lg mb-4">Récapitulatif</h3>
            <div className="flex justify-between text-gray-300"><span>Sous-total (TTC)</span><span data-testid="cart-subtotal">{total.toFixed(2)} €</span></div>
            <div className="flex justify-between text-gray-400 text-sm mt-2"><span>Livraison</span><span>Calculée au paiement</span></div>
            <div className="my-4 border-t border-white/10" />
            <div className="flex justify-between text-xl font-black"><span>Total</span><span className="text-[#E6007E]" data-testid="cart-total">{total.toFixed(2)} €</span></div>
            <Button data-testid="checkout-btn" onClick={checkout} disabled={loading} className="btn-primary w-full rounded-full mt-6 py-6 text-base font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Payer par carte</>}
            </Button>

            <div className="my-4 flex items-center gap-3 text-xs text-gray-500">
              <span className="h-px flex-1 bg-white/10" /> ou <span className="h-px flex-1 bg-white/10" />
            </div>

            <PayPalCheckout kind="order" getReferenceId={createOrderForPayment} onSuccess={handlePaypalPaid} />
            <p className="mt-3 text-xs text-center text-gray-500">Compte PayPal, carte bancaire (sans compte) ou paiement en plusieurs fois si éligible. Aucune donnée bancaire n'est stockée sur le site.</p>

            {!user && <p className="mt-3 text-xs text-center text-gray-500">Connexion requise pour finaliser la commande.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
